import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";

export const CUSTOM_AI_MODELS = Object.freeze([
  "gpt-5-nano",
  "gpt-5.4-mini",
  "gpt-5.6-luna",
  "gpt-6-luna",
  "gpt-6-sol",
]);

function clone(value) {
  return JSON.parse(JSON.stringify(value));
}

function sameConfiguration(left, right) {
  return JSON.stringify(left) === JSON.stringify(right);
}

function formatTime(value) {
  if (!value) return "Never";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "Unavailable" : date.toLocaleString(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
    timeZone: "UTC",
  }) + " UTC";
}

function publicSaveError(error) {
  if (["OUTCOME_UNKNOWN", "NOT_FOUND", "COMMAND_OFFLINE", "TRANSPORT_DRAINING", "SESSION_CHANGED"].includes(error?.code)) {
    return error.message || "The save outcome is not confirmed. Load the current revision before saving again.";
  }
  if (error?.code === "INVALID_ADMIN_RESPONSE") {
    return "The admin service returned invalid configuration data. Refresh and try again.";
  }
  if (error?.status === 400) return "The configuration was rejected. Check every value and try again.";
  if (error?.status === 401) return "Your admin session expired. Sign in again before saving.";
  if (error?.status === 403) return "The save could not be verified. Refresh the page and try again.";
  if (error?.status === 503) return "Configuration storage is temporarily unavailable. Try again shortly.";
  return "The configuration could not be saved. Try again.";
}

export function ConfigurationSyncSummary({ envelope, syncStatus, enabled }) {
  const desiredRevision = envelope?.revision ?? 0;
  const reportedDesiredRevision = syncStatus?.desiredRevision;
  const appliedRevision = syncStatus?.javaAppliedRevision ?? 0;
  const statusMatchesEnvelope = reportedDesiredRevision === desiredRevision;
  const exactError = statusMatchesEnvelope
    && typeof syncStatus?.lastJavaError === "string"
    && new RegExp(`revision\\s+${desiredRevision}(?:\\D|$)`, "i").test(syncStatus.lastJavaError)
    ? syncStatus.lastJavaError.replace(/\bJava\b/g, "App backend")
    : null;
  let statusMessage;
  let statusClass = "adminWaitingStatus";
  if (!statusMatchesEnvelope) statusMessage = "Synchronization status is stale; refresh required";
  else if (appliedRevision > desiredRevision) statusMessage = "Synchronization revisions are inconsistent; refresh required";
  else if (exactError) statusMessage = "App backend rejected the desired revision";
  else if (desiredRevision > appliedRevision) statusMessage = "Saved on droplet; waiting for App backend";
  else if (!syncStatus?.lastJavaAckAt) statusMessage = "Waiting for App backend acknowledgement";
  else {
    statusMessage = "Configuration is applied by App backend";
    statusClass = "adminAppliedStatus";
  }
  return (
    <section className="adminSyncCard" aria-label="Process and synchronization status">
      <div className="adminStatusRow">
        <span className={enabled ? "adminStatusBadge adminStatusRunning" : "adminStatusBadge"}>
          Desired state: {enabled ? "Enabled" : "Disabled"}
        </span>
        <strong className={statusClass}>{statusMessage}</strong>
      </div>
      <dl className="adminSyncGrid">
        <div><dt>Desired revision</dt><dd>{desiredRevision}</dd></div>
        <div><dt>App backend-applied revision</dt><dd>{appliedRevision}</dd></div>
        <div><dt>Saved by</dt><dd>{envelope?.savedByUserId ? "Administrator" : "System"}</dd></div>
        <div><dt>Saved on droplet</dt><dd>{formatTime(envelope?.savedAt)}</dd></div>
        <div><dt>Last App backend poll</dt><dd>{formatTime(syncStatus?.lastJavaPollAt)}</dd></div>
        <div><dt>Last App backend acknowledgement</dt><dd>{formatTime(syncStatus?.lastJavaAckAt)}</dd></div>
      </dl>
      {exactError && (
        <div className="adminAlert adminAlertError" role="alert">{exactError}</div>
      )}
    </section>
  );
}

export function useConfigurationEditor({
  api,
  documentKey,
  label,
  envelope: incomingEnvelope,
  onDirtyChange = () => {},
  onEnvelopeChange = () => {},
  onSavingChange = () => {},
  refreshVersion = 0,
  sessionGeneration = 0,
}) {
  const [envelope, setEnvelope] = useState(() => clone(incomingEnvelope));
  const [draft, setDraft] = useState(() => clone(incomingEnvelope.configuration));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [conflict, setConflict] = useState(null);
  const [conflictGeneration, setConflictGeneration] = useState(null);
  const [reloadRequired, setReloadRequired] = useState(false);
  const operationRef = useRef(0);
  const savingRef = useRef(false);
  const sessionGenerationRef = useRef(sessionGeneration);

  const dirty = useMemo(
    () => !sameConfiguration(draft, envelope.configuration),
    [draft, envelope],
  );

  useLayoutEffect(() => {
    operationRef.current += 1;
    savingRef.current = false;
    sessionGenerationRef.current = sessionGeneration;
    setSaving(false);
    const next = clone(incomingEnvelope);
    setEnvelope(next);
    setDraft(clone(next.configuration));
    setConflict(null);
    setConflictGeneration(null);
    setError("");
    setReloadRequired(false);
  }, [incomingEnvelope, refreshVersion, sessionGeneration]);

  useLayoutEffect(() => () => { operationRef.current += 1; }, []);

  useEffect(() => {
    onSavingChange(saving);
    return () => onSavingChange(false);
  }, [saving, onSavingChange]);

  useEffect(() => {
    onDirtyChange(dirty);
    return () => onDirtyChange(false);
  }, [dirty, onDirtyChange]);

  useEffect(() => {
    if (!dirty) return undefined;
    const warn = (event) => {
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [dirty]);

  const save = async () => {
    if (!dirty || savingRef.current || conflict || reloadRequired) return;
    savingRef.current = true;
    const operation = ++operationRef.current;
    const saveSessionGeneration = sessionGeneration;
    setSaving(true);
    setError("");
    try {
      const saved = await api.saveConfiguration(documentKey, clone(draft), envelope.revision);
      if (operation !== operationRef.current
        || saveSessionGeneration !== sessionGenerationRef.current) return;
      const next = clone(saved);
      setEnvelope(next);
      setDraft(clone(next.configuration));
      onEnvelopeChange(documentKey, next);
    } catch (saveError) {
      if (operation !== operationRef.current
        || saveSessionGeneration !== sessionGenerationRef.current) return;
      if (saveError?.status === 409 && saveError.current) {
        setConflict(clone(saveError.current));
        setConflictGeneration(saveSessionGeneration);
        setError(`Server revision ${saveError.current.revision} is newer. Reload it or explicitly keep your changes on that revision.`);
      } else {
        setError(publicSaveError(saveError));
        if (saveError?.requiresFreshLoad) setReloadRequired(true);
      }
    } finally {
      if (operation === operationRef.current) {
        savingRef.current = false;
        setSaving(false);
      }
    }
  };

  const reloadConflict = () => {
    if (savingRef.current || !conflict
      || conflictGeneration !== sessionGenerationRef.current
      || conflictGeneration !== sessionGeneration) return;
    const next = clone(conflict);
    setEnvelope(next);
    setDraft(clone(next.configuration));
    setConflict(null);
    setConflictGeneration(null);
    setError("");
    onEnvelopeChange(documentKey, next);
  };

  const keepDraftOnConflict = () => {
    if (savingRef.current || !conflict
      || conflictGeneration !== sessionGenerationRef.current
      || conflictGeneration !== sessionGeneration) return;
    const next = clone(conflict);
    setEnvelope(next);
    setConflict(null);
    setConflictGeneration(null);
    setError("");
  };

  const discard = () => {
    if (savingRef.current) return;
    if (dirty && !window.confirm(`Discard unsaved ${label} changes?`)) return;
    setDraft(clone(envelope.configuration));
    setConflict(null);
    setConflictGeneration(null);
    setError("");
  };

  return {
    envelope,
    draft,
    setDraft: (next) => { if (!savingRef.current) setDraft(next); },
    dirty,
    saving,
    error,
    conflict,
    reloadRequired,
    save,
    discard,
    reloadConflict,
    keepDraftOnConflict,
  };
}

function ConflictActions({ editor, label }) {
  if (!editor.conflict) return null;
  const revision = editor.conflict.revision;
  return (
    <div className="adminConflictActions">
      <button type="button" className="adminSecondaryButton" onClick={editor.reloadConflict}
        disabled={editor.saving}
        aria-label={`Reload ${label} revision ${revision}`}>
        Reload server revision {revision}
      </button>
      <button type="button" className="adminSecondaryButton" onClick={editor.keepDraftOnConflict}
        disabled={editor.saving}
        aria-label={`Keep my ${label} changes on revision ${revision}`}>
        Keep my changes on revision {revision}
      </button>
    </div>
  );
}

export function ConfigurationFormActions({
  editor,
  label,
  onRefresh,
  saveDisabled = false,
  validationMessage = null,
}) {
  return (
    <>
      {editor.error && <div className="adminAlert adminAlertError" role="alert">{editor.error}</div>}
      {validationMessage && <div className="adminAlert adminAlertError" role="alert">{validationMessage}</div>}
      <ConflictActions editor={editor} label={label} />
      <div className="adminFormActions">
        <button type="button" className="adminSecondaryButton" onClick={editor.discard}
          disabled={editor.saving || (!editor.dirty && !editor.conflict)} aria-label={`Discard ${label} changes`}>
          Discard changes
        </button>
        <button type="button" className="adminSecondaryButton" onClick={onRefresh}
          disabled={editor.saving}
          aria-label={`Refresh ${label} configuration`}>Refresh</button>
        <button type="submit" className="adminPrimaryButton"
          disabled={!editor.dirty || editor.saving || Boolean(editor.conflict)
            || editor.reloadRequired || saveDisabled}
          aria-label={`Save ${label} changes`}>
          {editor.saving ? "Saving…" : "Save changes"}
        </button>
      </div>
    </>
  );
}

export default function CustomAiConfigurationForm(props) {
  const editor = useConfigurationEditor({ ...props, documentKey: "customAi", label: "Custom AI" });
  const { draft, setDraft } = editor;
  return (
    <form className="adminConfigurationForm" onSubmit={(event) => { event.preventDefault(); editor.save(); }}>
      <ConfigurationSyncSummary envelope={editor.envelope} syncStatus={props.syncStatus}
        enabled={editor.envelope.configuration.enabled} />
      <fieldset className="adminFieldset" disabled={editor.saving}>
        <legend>Custom AI process</legend>
        <label className="adminCheckboxField">
          <input type="checkbox" aria-label="Enable Custom AI evaluations" checked={draft.enabled}
            onChange={(event) => setDraft({ ...draft, enabled: event.target.checked })} />
          <span><strong>Enable Custom AI evaluations</strong><small>Runs automatic setup evaluations.</small></span>
        </label>
        <label className="adminFormField">
          <span>Custom AI model</span>
          <select aria-label="Custom AI model" value={draft.model} onChange={(event) => setDraft({ ...draft, model: event.target.value })}>
            {CUSTOM_AI_MODELS.map((model) => <option key={model} value={model}>{model}</option>)}
          </select>
          <small>The model used for automatic setup evaluations.</small>
        </label>
        <label className="adminFormField">
          <span>Custom AI daily limit</span>
          <input type="number" aria-label="Custom AI daily limit" min="0" max="10000" step="1" required value={draft.dailyLimit}
            onChange={(event) => setDraft({ ...draft, dailyLimit: Number(event.target.value) })} />
          <small>Requests per UTC day. Use 0 to disable automatic evaluations.</small>
        </label>
      </fieldset>
      <ConfigurationFormActions editor={editor} label="Custom AI" onRefresh={props.onRefresh} />
    </form>
  );
}
